<script setup lang="ts">
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()
const isScreenSM = useMediaQuery('(min-width: 768px)')

function getLabelIcon(index: StringifiedInt<IntRange<0, 3>>) {
  return match(index)
    .with('0', () => 'bar-dotted' satisfies IconName)
    .with('1', () => 'heart-dotted' satisfies IconName)
    .with('2', () => 'zap-dotted' satisfies IconName)
    .exhaustive() as IconName
}

function getFeatureIcons(index: StringifiedInt<IntRange<0, 3>>) {
  return match(index)
    .with(
      '0',
      () => ['magnifier-outline', 'star-outline', 'trophy-outline'] satisfies IconName[],
    )
    .with(
      '1',
      () =>
        [
          'people-group-addition-outline',
          'calendar-check-outline',
          'sparks-outline',
        ] satisfies IconName[],
    )
    .with(
      '2',
      () =>
        ['fork-knife-outline', 'money-bag-outline', 'people-group-addition-outline'] satisfies IconName[],
    )
    .exhaustive() as IconName[]
}

function getAnimationComponent(index: StringifiedInt<IntRange<0, 3>>) {
  return match(index)
    .with('0', () => defineAsyncComponent(() => import('@/components/home/partial/GrowthAnimation.vue')))
    .with('1', () => defineAsyncComponent(() => import('@/components/home/partial/RememberedAnimation.vue')))
    .with('2', () => defineAsyncComponent(() => import('@/components/home/partial/RevenueAnimation.vue')))
    .exhaustive()
}
</script>

<template>
  <div class="bg-white">
    <section class="mx-auto max-w-screen-xl px-3 pt-16 md:px-8">
      <UiSectionHeadline :title="t('home.section.features.title')" />

      <div class="mt-12 flex flex-col gap-y-8">
        <div
          v-for="(item, itemIndex) in tm('home.section.features.items')"
          :key="itemIndex"
          class="grid grid-cols-1 overflow-clip rounded-[1.875rem] border border-neutral-200 lg:grid-cols-2 lg:grid-rows-1"
        >
          <div>
            <header
              class="flex items-center gap-x-1 border-b border-neutral-200 px-8 py-6"
            >
              <UiIcon
                :name="getLabelIcon(itemIndex)"
                class="text-xl text-orange-500"
              />

              <span class="text-lg font-medium">{{ rt(item.label) }}</span>
            </header>

            <div class="flex flex-col place-items-start gap-y-8 p-10">
              <h3 class="text-balance text-xl font-medium md:text-left lg:text-3xl">
                {{ rt(item.title) }}
              </h3>

              <ul class="flex flex-col gap-y-4">
                <li
                  v-for="(listItem, listItemIndex) in item.list"
                  :key="listItemIndex"
                  class="flex gap-x-2"
                >
                  <UiIcon
                    :name="getFeatureIcons(itemIndex)[listItemIndex]"
                    class="text-2xl text-orange-500"
                  />

                  <div class="flex flex-col">
                    <span class="text-lg font-semibold">
                      {{ rt(listItem.title) }}
                    </span>

                    <i18n-t
                      :keypath="`home.section.features.items.${itemIndex}.list.${listItemIndex}.description`"
                      tag="span"
                      class="text-neutral-500"
                    >
                      <template
                        v-if="listItem.placeholder"
                        #placeholder
                      >
                        <strong class="font-semibold text-orange-500">{{
                          rt(listItem.placeholder)
                        }}</strong>
                      </template>
                    </i18n-t>
                  </div>
                </li>
              </ul>

              <UiButton
                class="group w-full md:w-fit"
                :class="{
                  'hidden md:flex': Number(itemIndex) < Object.keys(tm('home.section.features.items')).length - 1,
                }"
                :color="Number(itemIndex) === Object.keys(tm('home.section.features.items')).length - 1
                  ? (!isScreenSM ? 'accent' : 'neutral')
                  : 'neutral'"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  {{ item.action }}

                  <UiIcon
                    name="arrow-outward-rounded"
                    class="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>

          <div
            class="relative select-none p-6 lg:p-12"
            :class="[
              'grid h-max bg-[#F8F8F0] md:h-auto',
              {
                'lg:-order-1': Number(itemIndex) % 2,
                'order-1': Number(itemIndex) % 2,
              },
            ]"
          >
            <Component :is="getAnimationComponent(itemIndex)" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
